import { FORWARD, REVERSE } from "configs/lottie";
import { IntersectionObserverOptions } from "hooks/useIntersectionObserver";
import useMediaQuery from "hooks/useMediaQuery";
import useOnScreen from "hooks/useOnScreen";
import { breakpoints, Text } from "iparque-components";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AppearFromBottomProps } from "theme/staggeredAnimations";
import { IParqueModule as ModuleInterface } from "../interfaces";
import { SPEED, STARTING_FRAME } from "./configs";
import { Container, TextContainer, UnderlinedText } from "./styles";

interface Props extends AppearFromBottomProps {
  iParqueModule: ModuleInterface;
}

const intersectionOptions: IntersectionObserverOptions = {
  threshold: 1,
};

const IParqueModule: React.FC<Props> = ({
  index,
  isSectionVisible,
  iParqueModule,
}) => {
  const { t } = useTranslation();
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isOnScreen = useOnScreen(containerRef, intersectionOptions);
  const isMobileScreen = useMediaQuery({ maxWidth: breakpoints.md });

  useEffect(() => {
    lottieRef.current?.setSpeed(SPEED);
  }, []);

  const playForwardAnimation = useCallback(() => {
    lottieRef.current?.setDirection(FORWARD);
    lottieRef.current?.goToAndPlay(STARTING_FRAME, true);
  }, []);

  const playReverseAnimation = useCallback(() => {
    lottieRef.current?.setDirection(REVERSE);
    lottieRef.current?.play();
  }, []);

  useEffect(() => {
    if (!isMobileScreen) {
      return;
    }

    if (isOnScreen) {
      playForwardAnimation();
    } else {
      playReverseAnimation();
    }
  }, [isMobileScreen, isOnScreen, playForwardAnimation, playReverseAnimation]);

  return (
    <Container
      ref={containerRef}
      onMouseEnter={playForwardAnimation}
      onMouseLeave={playReverseAnimation}
      index={index}
      isSectionVisible={isSectionVisible}
    >
      <Lottie
        lottieRef={lottieRef}
        animationData={iParqueModule.animationData}
        loop={false}
        autoplay={false}
        role="img"
        aria-labelledby={iParqueModule.ariaLabel}
        title={iParqueModule.ariaLabel}
      />
      <Text color="secondary">{iParqueModule.text}</Text>
      <TextContainer onClick={iParqueModule.onClick}>
        <UnderlinedText variant="body6" color="secondary" className="mt-20">
          {t("9162") /* Saber mais */}
        </UnderlinedText>
      </TextContainer>
    </Container>
  );
};

export default IParqueModule;
